import { formatWhen, TimeframeChip, IconChip, ForYouRow } from "./RecentChangeRows";
import { useState, type ComponentType } from "react";
import { Link } from "react-router-dom";
import {
  CircleCheck,
  CirclePlus,
  FilePlus2,
  Inbox,
  Layers,
  MessageSquare,
  PencilLine,
} from "lucide-react";

import { DashboardPanel } from "@/components/building/DashboardPanel";
import { chartAccentTint, type ChartAccent } from "@/components/charts/chartPalette";
import { SettingsSectionLabel } from "@/components/settings/SettingsSectionLabel";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { PMEmptyState } from "@/domains/pm/components/shared/PMEmptyState";
import { useProjectResponsibilities } from "@/domains/contacts/useResponsibilities";
import { itemsThatFit, remPx, useMeasuredHeight } from "@/hooks/useFittingCount";
import {
  useDashboardDocumentOpener,
  type DashboardDocumentOpener,
} from "@/domains/documents/hooks/useDashboardDocumentOpener";
import { useAuth } from "@/hooks/useAuth";
import { useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import { usePermissions } from "@/hooks/usePermissions";
import { useProjectDocumentCoverageState } from "@/hooks/useProjectDocumentCoverage";
import { useProjectFeatureAccess } from "@/hooks/useProjectFeatureAccess";
import { FEATURE_FLAGS, useFeatureFlag } from "@/lib/posthog";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { Translations } from "@/lib/i18n/types";
import { cn } from "@/lib/utils";

import type { DocumentExample, IssueExample } from "./examples";
import { issueHref } from "./forYou";
import {
  DEFAULT_RECENT_CHANGE_WINDOW,
  isRecentChangeWindow,
  type RecentChangeWindow,
  type WindowCount,
} from "./summary";
import { tileLayout, type TileLayout, type TileSize } from "./tileLayout";
import { useProjectRecentChanges } from "./useProjectRecentChanges";

interface RecentChangesWidgetProps {
  buildingId: number;
  /** The active project (`building_projects.id`). */
  projectId: number;
}

type Copy = Translations["buildings"]["projectsDashboard"]["recentChanges"];
type IconType = ComponentType<{ className?: string }>;

export const RECENT_CHANGES_WINDOW_STORAGE_KEY = "franklin:recent-changes-window:v1";
/** "For you" items listed; the rest are a "+N more" line. */
const VISIBLE_FOR_YOU = 8;

function loadWindow(): RecentChangeWindow {
  try {
    const raw = window.localStorage.getItem(RECENT_CHANGES_WINDOW_STORAGE_KEY);
    return isRecentChangeWindow(raw) ? raw : DEFAULT_RECENT_CHANGE_WINDOW;
  } catch {
    return DEFAULT_RECENT_CHANGE_WINDOW;
  }
}

function saveWindow(value: RecentChangeWindow): void {
  try {
    window.localStorage.setItem(RECENT_CHANGES_WINDOW_STORAGE_KEY, value);
  } catch {
    // Persistence is a nicety; ignore quota/privacy-mode failures.
  }
}

type Example = IssueExample | DocumentExample;

interface SummaryLine {
  key: string;
  icon: IconType;
  accent: ChartAccent;
  label: string;
  value: WindowCount | undefined;
  href: string;
  /** The items behind the count, newest first — shown in the spotlight layout. */
  examples: Example[];
}

function SummaryRow({ line, atLeastHint }: { line: SummaryLine; atLeastHint: string }) {
  return (
    <li>
      <Link
        to={line.href}
        className="flex items-center gap-3 px-4 py-2 text-sm transition-colors hover:bg-[#efeff1]"
      >
        <IconChip icon={line.icon} accent={line.accent} />
        <span className="min-w-0 flex-1 truncate text-neutral-700">{line.label}</span>
        <span
          className="flex-shrink-0 font-medium tabular-nums text-neutral-900"
          title={line.value?.atLeast ? atLeastHint : undefined}
        >
          {formatCount(line.value)}
        </span>
      </Link>
    </li>
  );
}

const COUNT_TEXT: Record<TileSize, string> = {
  sm: "text-lg",
  md: "text-xl",
  lg: "text-2xl",
};

/** Full class strings per column count, so Tailwind's scanner keeps them. */
const GRID_COLUMNS: Record<TileLayout["columns"], string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  // Five tiles leave one cell of the second row empty: the last tile spans it.
  3: "grid-cols-1 sm:grid-cols-3 sm:[&>*:last-child:nth-child(3n+2)]:col-span-2",
};

function formatCount(value: WindowCount | undefined): string {
  return value ? `${value.count}${value.atLeast ? "+" : ""}` : "–";
}

function ExampleRow({
  example,
  buildingId,
  projectId,
  opener,
  stacked,
}: {
  example: Example;
  buildingId: number;
  projectId: number;
  opener: DashboardDocumentOpener;
  /** Narrow tile: the time goes under the title instead of beside it. */
  stacked: boolean;
}) {
  const { language } = useLanguage();
  const when = formatWhen(example.at, language);
  // Fixed heights (EXAMPLE_ROW_REM) so `itemsThatFit` can count them exactly.
  const rowClass = cn(
    "-mx-2 flex w-[calc(100%+1rem)] items-center gap-2 rounded-md px-2 text-left text-sm",
    stacked ? "h-11" : "h-7"
  );
  const metaClass = cn(
    "truncate text-[11px] text-muted-foreground",
    stacked ? "block" : "flex-shrink-0"
  );

  if (example.kind === "issue") {
    return (
      <li>
        <Link
          to={issueHref(buildingId, projectId, example.issueId)}
          className={cn(rowClass, "transition-colors hover:bg-[#efeff1]")}
        >
          <span className={cn("min-w-0 flex-1", !stacked && "contents")}>
            <span
              className={cn("min-w-0 truncate text-neutral-800", stacked ? "block" : "flex-1")}
              title={example.subject}
            >
              {example.subject}
            </span>
            <span className={metaClass}>
              {example.actorName ? `${example.actorName} · ${when}` : when}
            </span>
          </span>
        </Link>
      </li>
    );
  }

  const doc = example.document;
  const body = (
    <>
      {/* h-6, as in "Uploaded today": the chip's label is a fixed 9px, and a
          three-letter extension (DWG) overflows anything narrower. */}
      <FileTypeIcon filename={doc.filename} className="h-6 w-6 flex-shrink-0" />
      <span className={cn("min-w-0 flex-1", !stacked && "contents")}>
        <span
          className={cn("min-w-0 truncate text-neutral-800", stacked ? "block" : "flex-1")}
          title={doc.filename}
        >
          {doc.filename}
        </span>
        <span className={metaClass}>{when}</span>
      </span>
    </>
  );
  return (
    <li>
      {opener.canOpen(doc) ? (
        <button
          type="button"
          onClick={() => opener.open(doc)}
          className={cn(rowClass, "transition-colors hover:bg-[#efeff1]")}
        >
          {body}
        </button>
      ) : (
        <div className={rowClass}>{body}</div>
      )}
    </li>
  );
}

/**
 * Item row heights in rem — `h-7` beside its time, `h-11` with the time below
 * — and the "+N more" line (`h-5`), reserved only when something is left out.
 * Pixels come from `remPx()`: the app scales its root font.
 */
const EXAMPLE_ROW_REM = { inline: 1.75, stacked: 2.75 } as const;
const EXAMPLE_MORE_REM = 1.25;

/**
 * One count as a tile: its icon, the number, and — when the layout lists items
 * — as many of the newest items behind it as fit the tile's height, then
 * "+N more".
 */
function CountTile({
  line,
  size,
  stacked,
  showExamples,
  buildingId,
  projectId,
  opener,
  copy,
}: {
  line: SummaryLine;
  size: TileSize;
  /** Narrow (three-column) tile: items put their time on a second line. */
  stacked: boolean;
  showExamples: boolean;
  buildingId: number;
  projectId: number;
  opener: DashboardDocumentOpener;
  copy: Copy;
}) {
  // An absolutely placed box filling what the tile has left, so the rows
  // inside never decide its height — the card's does.
  const [areaRef, areaHeight] = useMeasuredHeight<HTMLDivElement>();
  const fit = showExamples && line.examples.length > 0;
  const shownCount = fit
    ? itemsThatFit(areaHeight, line.examples.length, {
        rowPx: (stacked ? EXAMPLE_ROW_REM.stacked : EXAMPLE_ROW_REM.inline) * remPx(),
        morePx: EXAMPLE_MORE_REM * remPx(),
        beforeMeasure: 3,
      })
    : 0;
  const shown = line.examples.slice(0, shownCount);
  const more = (line.value?.count ?? line.examples.length) - shown.length;

  const list = (
    <>
      <ul>
        {shown.map((example) => (
          <ExampleRow
            key={example.key}
            example={example}
            buildingId={buildingId}
            projectId={projectId}
            opener={opener}
            stacked={stacked}
          />
        ))}
      </ul>
      {more > 0 && (
        <Link
          to={line.href}
          className="flex h-5 items-center text-xs font-medium text-primary hover:underline"
        >
          {fill(copy.moreItems, { count: String(more) })} →
        </Link>
      )}
    </>
  );

  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-3 bg-white p-4",
        !fit && shown.length === 0 && "justify-center"
      )}
      data-testid={`spotlight-${line.key}`}
    >
      <Link to={line.href} className="group flex min-w-0 items-center gap-3">
        <IconChip icon={line.icon} accent={line.accent} size={size} />
        <span className="min-w-0">
          <span
            className={cn(
              "block font-semibold tabular-nums leading-none text-neutral-900",
              COUNT_TEXT[size]
            )}
            title={line.value?.atLeast ? copy.atLeastHint : undefined}
          >
            {formatCount(line.value)}
          </span>
          <span className="mt-1 block truncate text-sm text-neutral-600 group-hover:underline">
            {line.label}
          </span>
        </span>
      </Link>
      {fit && (
        // Never less than one whole item and the "+N more" line (h-7/h-11 +
        // h-5), so a tile squeezed by "For you" still shows one item intact.
        <div className={cn("relative flex-1", stacked ? "min-h-16" : "min-h-12")}>
          {/* The clip box is widened by the rows' -mx-2 (and padded back), so
              it cuts rows off at the bottom only — never a row's left edge or
              its file-type chip. */}
          <div ref={areaRef} className="absolute -inset-x-2 inset-y-0 overflow-hidden px-2">
            {list}
          </div>
        </div>
      )}
    </div>
  );
}

/** Lines still loading show "–"; once loaded, a zero line is left out. */
function shownLines(lines: SummaryLine[], loading: boolean): SummaryLine[] {
  return loading ? lines : lines.filter((line) => (line.value?.count ?? 0) > 0);
}

/**
 * Project-dashboard "What's new", beside the message board: one block over a
 * timeframe the viewer picks (24 h / 72 h / 7 days / 30 days, remembered per
 * browser). On top, what changed in the project — new, completed and updated
 * issues, comments, new documents and versions, each linking to its page; zero
 * counts are left out. The counts are tiles sized by how many there are
 * (`tileLayout`): one or two get large tiles with the newest items behind
 * them, more share a grid, and nothing new is a centred empty state — never a
 * line or two over an empty card. Below,
 * "For you": comments that mention the viewer,
 * issues assigned to them, and new issues in the disciplines they are
 * responsible for on /contacts.
 *
 * Issue lines and the issue side of "For you" follow My tasks' gate — the PM
 * module, the PM tool permission and the member's access to this project's
 * issues. Document lines follow Document analytics: a viewer with hidden
 * folders gets the reason instead of a count that would pass for the
 * project's. Document-comment mentions are shown to anyone RLS lets read them.
 */
export function RecentChangesWidget({ buildingId, projectId }: RecentChangesWidgetProps) {
  const { t } = useLanguage();
  const perms = usePermissions();
  const { userProfile } = useAuth();
  const pmModuleEnabled = useFeatureFlag(FEATURE_FLAGS.PM_MODULE_ENABLED, true);
  const { projectOptions, optionsLoaded } = useBuildingScope(String(buildingId));
  const featureAccess = useProjectFeatureAccess({
    selectedProjectId: projectId,
    projectOptions,
    optionsLoaded,
  });
  const coverage = useProjectDocumentCoverageState(projectId);
  const { myDisciplineIds } = useProjectResponsibilities(buildingId, projectId);
  const [timeframe, setTimeframe] = useState<RecentChangeWindow>(loadWindow);
  const opener = useDashboardDocumentOpener(String(buildingId));

  const showIssues = pmModuleEnabled && perms.canViewPMTool && featureAccess.allowed("issues");
  const showDocumentCounts = coverage.ready && coverage.coverage === "full";

  const { issues, documents, forYou } = useProjectRecentChanges({
    buildingId,
    projectId,
    window: timeframe,
    userId: userProfile?.id,
    myDisciplineIds,
    issuesEnabled: showIssues,
    documentsEnabled: showDocumentCounts,
  });

  const copy = t.buildings.projectsDashboard.recentChanges;
  const issuesHref = `/buildings/${buildingId}/issues?project=${projectId}`;
  const documentsHref = `/buildings/${buildingId}/documents?project=${projectId}`;
  const s = copy.summary;

  const issueLines: SummaryLine[] = showIssues
    ? [
        {
          key: "newIssues",
          icon: CirclePlus,
          accent: "rose" as const,
          examples: issues.examples?.opened ?? [],
          label: s.newIssues,
          value: issues.summary?.opened,
        },
        {
          key: "completedIssues",
          icon: CircleCheck,
          accent: "teal" as const,
          examples: issues.examples?.completed ?? [],
          label: s.completedIssues,
          value: issues.summary?.completed,
        },
        {
          key: "updatedIssues",
          icon: PencilLine,
          accent: "indigo" as const,
          examples: issues.examples?.updated ?? [],
          label: s.updatedIssues,
          value: issues.summary?.updated,
        },
        {
          key: "comments",
          icon: MessageSquare,
          accent: "purple" as const,
          examples: issues.examples?.comments ?? [],
          label: s.comments,
          value: issues.summary?.comments,
        },
      ].map((line) => ({ ...line, href: issuesHref }))
    : [];
  const documentLines: SummaryLine[] = showDocumentCounts
    ? [
        {
          key: "newDocuments",
          icon: FilePlus2,
          accent: "steel" as const,
          examples: documents.examples?.added ?? [],
          label: s.newDocuments,
          value: documents.summary?.added,
        },
        {
          key: "newVersions",
          icon: Layers,
          accent: "coral" as const,
          examples: documents.examples?.newVersions ?? [],
          label: s.newVersions,
          value: documents.summary?.newVersions,
        },
      ].map((line) => ({ ...line, href: documentsHref }))
    : [];

  const issuesLoading = showIssues && issues.isLoading;
  const documentsLoading = showDocumentCounts && documents.isLoading;
  const visibleLines = [
    ...shownLines(issueLines, issuesLoading),
    ...shownLines(documentLines, documentsLoading),
  ];
  // Until roles are known, "no issue access" and "part of the documents" are
  // only the safe defaults: saying "nothing changed" or "not shown" then would
  // be a claim the next frame contradicts, so the card waits instead.
  const loading =
    !featureAccess.ready ||
    coverage.isLoading ||
    issuesLoading ||
    documentsLoading ||
    forYou.isLoading;
  const failed =
    coverage.isError ||
    (showIssues && issues.isError) ||
    (showDocumentCounts && documents.isError) ||
    forYou.isError;
  const forYouVisible = forYou.items.slice(0, VISIBLE_FOR_YOU);
  const forYouMore = forYou.items.length - forYouVisible.length;
  const hasForYou = forYou.items.length > 0;

  // Once settled, the counts are tiles sized by how many there are (see
  // tileLayout), never a line or two over an empty card. The list is only the
  // loading/failed frame.
  const settled = !loading && !failed;
  const tiles = settled && visibleLines.length > 0;
  const layout = tileLayout(visibleLines.length);
  const nothingNew = settled && visibleLines.length === 0;

  const handleTimeframe = (next: RecentChangeWindow) => {
    setTimeframe(next);
    saveWindow(next);
  };

  return (
    <section className="flex min-w-0 flex-col" data-testid="recent-changes">
      <div className="flex items-center justify-between gap-2">
        <SettingsSectionLabel>{copy.title}</SettingsSectionLabel>
        <TimeframeChip value={timeframe} onChange={handleTimeframe} copy={copy} />
      </div>
      {/* Same height bounds as the message board beside it, so the two share
          one row height; a long "For you" list scrolls inside the card. */}
      <DashboardPanel
        className="mt-3 flex max-h-[420px] min-h-[220px] flex-1 flex-col overflow-y-auto p-0"
        aria-busy={loading || undefined}
      >
        {tiles ? (
          <div
            className={cn(
              // gap-px over a tinted background draws hairlines between tiles.
              // The grid takes whatever height "For you" leaves, and the tiles
              // list as many items as fit in it.
              "grid flex-1 auto-rows-fr gap-px bg-neutral-100",
              GRID_COLUMNS[layout.columns]
            )}
            data-testid="recent-changes-spotlight"
          >
            {visibleLines.map((line) => (
              <CountTile
                key={line.key}
                line={line}
                size={layout.size}
                stacked={layout.columns === 3}
                showExamples={layout.showExamples}
                buildingId={buildingId}
                projectId={projectId}
                opener={opener}
                copy={copy}
              />
            ))}
          </div>
        ) : nothingNew && !hasForYou ? (
          // The same empty state as the Messages, My tasks and Brief cards beside it.
          <div className="flex flex-1 flex-col" data-testid="recent-changes-nothing-new">
            <PMEmptyState
              icon={Inbox}
              title={copy.nothingChanged}
              className="flex-1 px-4 py-8"
              iconColor={chartAccentTint("purple", 0.7)}
            />
          </div>
        ) : (
          <ul className="py-1.5" data-testid="recent-changes-summary">
            {visibleLines.map((line) => (
              <SummaryRow key={line.key} line={line} atLeastHint={copy.atLeastHint} />
            ))}
            {nothingNew && (
              <li className="px-4 py-2 text-sm text-muted-foreground">{copy.nothingChanged}</li>
            )}
            {failed && <li className="px-4 py-2 text-sm text-red-600">{copy.loadFailed}</li>}
          </ul>
        )}
        {coverage.ready && !showDocumentCounts && (
          <p
            className="border-t border-neutral-100 px-4 py-2 text-xs text-muted-foreground"
            data-testid="recent-changes-documents-unavailable"
          >
            {copy.documentsPartialAccess}
          </p>
        )}

        {hasForYou && (
          <div
            className="shrink-0 border-t border-neutral-200 py-1.5"
            data-testid="recent-changes-for-you"
          >
            <div className="flex items-center gap-2 px-4 pb-1 pt-1.5">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {copy.forYou.title}
              </h4>
              <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground">
                {forYou.items.length}
              </span>
            </div>
            <ul>
              {forYouVisible.map((item) => (
                <ForYouRow key={item.key} item={item} copy={copy} />
              ))}
            </ul>
            {forYouMore > 0 && (
              <p className="px-4 pb-1 pt-1 text-xs text-muted-foreground">
                {fill(copy.forYou.more, { count: String(forYouMore) })}
              </p>
            )}
          </div>
        )}
        {opener.dialog}
      </DashboardPanel>
    </section>
  );
}
